import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useCurrencies } from '../hooks/useCurrencies';
import DataTable from '../components/DataTable';
import { fmtAmount } from '../utils/numberFormat';
import SearchableSelect from '../components/SearchableSelect';
import CariCard from '../components/CariCard';
import { formatCariOption } from '../utils/cariFormat';
import QuickListManager from '../components/QuickListManager';
import { Button, Modal, FormField, ListPageHeader, ListPageStats } from '../components/ui';
import { CardHero, CardStatusBadge, CardSplitButton } from '../components/card';
import { TAB_ICONS } from '../utils/tabIcons';

const LOCAL_CURRENCY = 'TRY';

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Nakit', color: '#16a34a' },
  { value: 'bank_transfer', label: 'Banka Havalesi', color: '#3b82f6' },
  { value: 'check', label: 'Çek', color: '#7c3aed' },
  { value: 'note', label: 'Senet', color: '#d97706' },
  { value: 'credit_card', label: 'Kredi Kartı', color: '#dc2626' },
  { value: 'shopping_voucher', label: 'Alışveriş Çeki', color: '#0284c7' },
];

const cariLabel = (a) => `${a.code || ''} - ${a.title || ''}`;
const cariSub = (a) => {
  const p = [];
  if (a.phone || a.mobile) p.push(a.phone || a.mobile);
  if (a.identity_type === 'individual') { if (a.id_number) p.push('TC: ' + a.id_number); }
  else { if (a.tax_office) p.push(a.tax_office); if (a.tax_number) p.push(a.tax_number); }
  return p.filter(Boolean).join(' | ');
};
const cariBalance = (a) => {
  const bal = parseFloat(a.balance) || 0;
  if (Math.abs(bal) < 0.005) return null;
  const cur = a.currency_code || 'TRY';
  const txt = Number(Math.abs(bal)).toLocaleString('tr-TR', { minimumFractionDigits: 2 });
  return bal > 0
    ? { text: `Borc: ${txt} ${cur}`,    color: 'var(--danger)' }
    : { text: `Alacak: ${txt} ${cur}`,  color: 'var(--success)' };
};

const emptyLine = {
  payment_method: 'cash', amount: 0, currency_code: 'TRY', exchange_rate: 1,
  cash_register_id: '', bank_account_id: '',
  check_doc_number: '', check_serial_no: '', check_issuer_name: '',
  check_bank_name: '', check_branch_name: '', check_account_number: '',
  check_issue_date: '', check_due_date: '',
  notes: '',
};

function fmt(n) { return fmtAmount(n); }

function BalancePill({ account }) {
  const bal = parseFloat(account?.balance) || 0;
  const cur = account?.currency_code || 'TRY';
  const isDebit = bal > 0, isCredit = bal < 0;
  const color = isDebit ? '#dc2626' : isCredit ? '#16a34a' : '#64748b';
  const bg    = isDebit ? 'rgba(220,38,38,0.10)' : isCredit ? 'rgba(22,163,74,0.10)' : 'rgba(100,116,139,0.08)';
  const label = isDebit ? 'Cari bize borçlu' : isCredit ? 'Cari bizden alacaklı' : 'Bakiye sıfır';
  const dir   = isDebit ? '🔴 BORÇ' : isCredit ? '🟢 ALACAK' : '⚪ KAPALI';
  return (
    <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, background: bg,
                  border: `1px solid ${color}33`, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12 }}>
      <span style={{ fontWeight: 700, color, letterSpacing: '0.04em' }}>{dir}</span>
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ marginLeft: 'auto', fontFamily: 'monospace', fontWeight: 700, color, fontSize: 14 }}>
        {fmtAmount(Math.abs(bal))} {cur}
      </span>
    </div>
  );
}

function CariReceiptPage({ autoOpenId = null }) {
  const { currencies } = useCurrencies();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState(null);

  const [accounts, setAccounts] = useState([]);
  const [cashRegisters, setCashRegisters] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [posList, setPosList] = useState([]);
  const [posCommissions, setPosCommissions] = useState({});
  const [availableVouchers, setAvailableVouchers] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [projects, setProjects] = useState([]);
  const [specialCodes1, setSpecialCodes1] = useState([]);
  const [specialCodes2, setSpecialCodes2] = useState([]);
  const [settings, setSettings] = useState({});
  const [exchangeRates, setExchangeRates] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState('view'); // 'view' | 'add'
  const [selected, setSelected] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [header, setHeader] = useState({ account_id: '', date: today, description: '', notes: '',
    personnel_id: '', project_id: '', special_code1: '', special_code2: '' });
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/cari-receipts', { params: { search } });
      if (r.data.success) setReceipts(r.data.data.items);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [search]);

  const fetchLookups = useCallback(async () => {
    try {
      const [a, c, b, p, s, pe, pr] = await Promise.all([
        api.get('/accounts', { params: { limit: 500 } }),
        api.get('/cash-registers'),
        api.get('/bank-accounts'),
        api.get('/bank-pos'),
        api.get('/settings'),
        api.get('/personnel', { params: { limit: 200 } }),
        api.get('/invoices/lookups/all').catch(() => ({ data: { success: false } })),
      ]);
      if (a.data.success) setAccounts((a.data.data.items || []).filter(x => x.is_active));
      if (c.data.success) setCashRegisters((c.data.data.items || []).filter(x => x.is_active));
      if (b.data.success) setBankAccounts((b.data.data.items || []).filter(x => x.is_active));
      if (p.data.success) setPosList((p.data.data.items || []).filter(x => x.is_active));
      if (s.data.success) setSettings(s.data.data);
      if (pe.data.success) setPersonnel((pe.data.data.items || []).filter(x => x.is_active));
      if (pr.data.success) {
        setProjects(pr.data.data?.projects || []);
        setSpecialCodes1(pr.data.data?.specialCodes1 || []);
        setSpecialCodes2(pr.data.data?.specialCodes2 || []);
        setExchangeRates(pr.data.data?.exchangeRates || {});
      }
    } catch {}
  }, []);

  const refetchLookups = async () => {
    try {
      const r = await api.get('/invoices/lookups/all');
      if (r.data.success) {
        setProjects(r.data.data?.projects || []);
        setSpecialCodes1(r.data.data?.specialCodes1 || []);
        setSpecialCodes2(r.data.data?.specialCodes2 || []);
      }
    } catch {}
  };

  const [showQuickList, setShowQuickList] = useState(null);
  const [showAccountList, setShowAccountList] = useState(false);
  const [accountListSearch, setAccountListSearch] = useState('');

  const loadPosCommissions = async (posId) => {
    if (!posId || posCommissions[posId]) return;
    try {
      const r = await api.get('/bank-pos/' + posId);
      if (r.data.success) {
        setPosCommissions(prev => ({ ...prev, [posId]: r.data.data.commissions || [] }));
      }
    } catch {}
  };

  useEffect(() => {
    if (!header.account_id) { setAvailableVouchers([]); return; }
    api.get('/shopping-vouchers/available/' + header.account_id).then(r => {
      if (r.data.success) setAvailableVouchers(r.data.data);
    }).catch(() => setAvailableVouchers([]));
  }, [header.account_id]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const fetchStats = useCallback(async () => {
    try {
      const r = await api.get('/cari-receipts/stats');
      if (r.data.success) setStats(r.data.data);
    } catch {}
  }, []);
  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchLookups(); }, [fetchLookups]);

  const openNew = () => {
    const defCash = settings.default_cash_register_id && cashRegisters.find(r => r.id === settings.default_cash_register_id)
      ? settings.default_cash_register_id
      : (cashRegisters[0]?.id || '');
    setHeader({ account_id: '', date: today, description: '', notes: '',
      personnel_id: '', project_id: '', special_code1: '', special_code2: '' });
    setLines([{ ...emptyLine, cash_register_id: defCash }]);
    setMode('add'); setSelected(null); setMsg(null); setShowModal(true);
  };

  // Deep-link: autoOpenId varsa o makbuzu aç
  useEffect(() => {
    if (!autoOpenId) return;
    openDetail({ id: autoOpenId });
  }, [autoOpenId]);

  const openDetail = async (rec) => {
    try {
      const r = await api.get('/cari-receipts/' + rec.id);
      if (r.data.success) {
        const d = r.data.data;
        setSelected(d);
        setHeader({
          account_id: d.account_id, date: d.date?.slice(0, 10) || today,
          description: d.description || '', notes: d.notes || '',
          personnel_id: d.personnel_id || '', project_id: d.project_id || '',
          special_code1: d.special_code1 || '', special_code2: d.special_code2 || '',
        });
        setLines(d.lines || []);
        setMode('view'); setMsg(null); setShowModal(true);
      }
    } catch (err) { console.error(err); }
  };

  const closeModal = () => { setShowModal(false); setSelected(null); setMsg(null); };

  const lineLocal = (l) => (parseFloat(l.amount) || 0) * (parseFloat(l.exchange_rate) || 1);
  const total = lines.reduce((s, l) => s + lineLocal(l), 0);
  const hasFx = lines.some(l => l.currency_code && l.currency_code !== LOCAL_CURRENCY);

  const addLine = () => {
    const defCash = settings.default_cash_register_id && cashRegisters.find(r => r.id === settings.default_cash_register_id)
      ? settings.default_cash_register_id
      : (cashRegisters[0]?.id || '');
    setLines([...lines, { ...emptyLine, cash_register_id: defCash }]);
  };

  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));

  const setLine = (idx, key, val) => {
    setLines(prev => {
      const n = [...prev];
      n[idx] = { ...n[idx], [key]: val };
      return n;
    });
  };

  const fetchRateForCurrency = async (currencyCode, date) => {
    if (!currencyCode || currencyCode === LOCAL_CURRENCY) return '1';
    const cached = exchangeRates[currencyCode];
    const cachedRate = cached?.selling_rate || cached?.mid_rate || cached?.buying_rate;
    try {
      const r = await api.get('/exchange/rates/lookup', { params: { date: date || header.date, currency_code: currencyCode } });
      if (r.data.success && r.data.data) {
        const d = r.data.data;
        const rate = d.selling_rate || d.mid_rate || d.buying_rate || d.rate;
        if (rate) return String(rate);
      }
    } catch {}
    return cachedRate ? String(cachedRate) : '1';
  };

  const setLineCurrency = async (idx, code) => {
    const rate = await fetchRateForCurrency(code, header.date);
    setLines(prev => {
      const n = [...prev];
      n[idx] = { ...n[idx], currency_code: code, exchange_rate: rate };
      return n;
    });
  };

  const setLineCashRegister = async (idx, registerId) => {
    const reg = cashRegisters.find(r => r.id === registerId);
    const code = reg?.currency_code || LOCAL_CURRENCY;
    const rate = await fetchRateForCurrency(code, header.date);
    setLines(prev => {
      const n = [...prev];
      n[idx] = { ...n[idx], cash_register_id: registerId, currency_code: code, exchange_rate: rate };
      return n;
    });
  };

  const setLineBankAccount = async (idx, bankId) => {
    const ba = bankAccounts.find(b => b.id === bankId);
    const code = ba?.currency_code || LOCAL_CURRENCY;
    const rate = await fetchRateForCurrency(code, header.date);
    setLines(prev => {
      const n = [...prev];
      n[idx] = { ...n[idx], bank_account_id: bankId, currency_code: code, exchange_rate: rate };
      return n;
    });
  };

  const isCurrencyLocked = (method) => method === 'credit_card' || method === 'shopping_voucher';

  const setLinePaymentMethod = (idx, method) => {
    setLines(prev => {
      const n = [...prev];
      const cur = n[idx];
      const update = { ...cur, payment_method: method };
      if (isCurrencyLocked(method)) {
        update.currency_code = LOCAL_CURRENCY;
        update.exchange_rate = '1';
      }
      n[idx] = update;
      return n;
    });
  };

  const handleSave = async (afterMode = 'close') => {
    if (afterMode && typeof afterMode === 'object') afterMode = 'close';
    const currentMode = mode;
    if (!header.account_id) { setMsg({ type: 'error', text: 'Cari seçin' }); return; }
    if (lines.length === 0) { setMsg({ type: 'error', text: 'En az bir ödeme satırı ekleyin' }); return; }
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      const no = i + 1;
      if (!l.payment_method) { setMsg({ type: 'error', text: `Satır ${no}: ödeme yöntemi seçin` }); return; }
      if (!l.amount || parseFloat(l.amount) <= 0) { setMsg({ type: 'error', text: `Satır ${no}: geçerli tutar girin` }); return; }

      if (l.payment_method === 'cash') {
        if (!l.cash_register_id) { setMsg({ type: 'error', text: `Satır ${no} (Nakit): kasa seçin` }); return; }
        const reg = cashRegisters.find(r => r.id === l.cash_register_id);
        if (reg && l.currency_code && reg.currency_code !== l.currency_code) {
          setMsg({ type: 'error', text: `Satır ${no} (Nakit): "${reg.register_name}" kasası ${reg.currency_code} cinsindendir. ${reg.currency_code} kasası seçin veya satır dövizini ${reg.currency_code} yapın.` });
          return;
        }
      } else if (l.payment_method === 'bank_transfer') {
        if (!l.bank_account_id) { setMsg({ type: 'error', text: `Satır ${no} (Banka Havalesi): banka hesabı seçin` }); return; }
        const ba = bankAccounts.find(b => b.id === l.bank_account_id);
        if (ba && l.currency_code && ba.currency_code !== l.currency_code) {
          setMsg({ type: 'error', text: `Satır ${no} (Banka Havalesi): "${ba.account_name}" hesabı ${ba.currency_code} cinsindendir. ${ba.currency_code} hesap seçin veya satır dövizini ${ba.currency_code} yapın.` });
          return;
        }
      } else if (l.payment_method === 'check' || l.payment_method === 'note') {
        const tip = l.payment_method === 'check' ? 'Çek' : 'Senet';
        const eksik = [];
        if (!l.check_doc_number || !l.check_doc_number.trim()) eksik.push('Belge No');
        if (!l.check_due_date) eksik.push('Vade Tarihi');
        if (!l.check_bank_name || !l.check_bank_name.trim()) eksik.push('Banka');
        if (eksik.length) { setMsg({ type: 'error', text: `Satır ${no} (${tip}): ${eksik.join(', ')} eksik` }); return; }
      } else if (l.payment_method === 'credit_card') {
        const inst = parseInt(l.installment_count) || 1;
        l.installment_count = inst;
        if (!l.pos_definition_id) { setMsg({ type: 'error', text: `Satır ${no} (Kredi Kartı): POS eksik` }); return; }
      } else if (l.payment_method === 'shopping_voucher') {
        if (!l.shopping_voucher_id) { setMsg({ type: 'error', text: `Satır ${no} (Alış Veriş Çeki): çek seçin` }); return; }
      }
    }

    setSaving(true); setMsg(null);
    try {
      const payload = {
        account_id: header.account_id,
        date: header.date,
        description: header.description,
        notes: header.notes,
        personnel_id: header.personnel_id || null,
        project_id: header.project_id || null,
        special_code1: header.special_code1 || null,
        special_code2: header.special_code2 || null,
        lines,
      };
      const editing = currentMode === 'edit';
      const url = editing ? '/cari-receipts/' + selected.id : '/cari-receipts';
      const r = editing ? await api.put(url, payload) : await api.post(url, payload);
      if (r.data.success) {
        fetchList();
        if (editing) {
          await openDetail({ id: selected.id });
          setMsg({ type: 'success', text: 'Güncellendi' });
          setTimeout(() => setMsg(null), 2000);
        } else if (afterMode === 'new') {
          // Yeni kayıt — modal açık kalsın, formu sıfırla
          setHeader(prev => ({ ...prev, account_id: '', description: '', notes: '' }));
          setLines([]);
          setMsg({ type: 'success', text: 'Kaydedildi — yeni dekont' });
          setTimeout(() => setMsg(null), 2000);
        } else {
          closeModal();
        }
      }
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.message || 'Kaydedilemedi' }); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected || !window.confirm('Makbuz silinsin mi? Tüm bağlı hareketler geri alınacak.')) return;
    try {
      await api.delete('/cari-receipts/' + selected.id);
      closeModal(); fetchList();
    } catch (err) { alert(err.response?.data?.message || 'Silinemedi'); }
  };

  // Modal aksiyonları — footer'a taşındı (yeni Modal API)
  const isAdd  = mode === 'add';
  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const isEditable = isAdd || isEdit;

  const modalTitle = isAdd  ? 'Yeni Tahsilat Makbuzu'
                   : isEdit ? `Düzenle — ${selected?.receipt_no || ''}`
                   :          (selected?.receipt_no || 'Makbuz');

  const enterEditMode = () => { setMode('edit'); setMsg(null); };

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Cari Tahsilat Makbuzları"
        count={receipts.length}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Makbuz no, cari..."
        onRefresh={() => { fetchList(); fetchStats(); }}
        refreshing={loading}
        actions={<Button size="sm" onClick={openNew}>+ Yeni Tahsilat</Button>}
      />

      <ListPageStats
        loading={stats == null}
        stats={[
          {
            key: 'month',
            label: 'Bu Ay Tahsilat',
            value: stats?.month_total,
            sublabel: `${stats?.month_count || 0} makbuz`,
            color: 'success',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
          },
          {
            key: 'week',
            label: 'Son 7 Gün',
            value: stats?.week_total,
            sublabel: `${stats?.week_count || 0} makbuz`,
            color: 'primary',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
          },
          {
            key: 'm30',
            label: 'Son 30 Gün',
            value: stats?.m30_count,
            sublabel: 'makbuz adedi',
            color: 'neutral',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
          },
          {
            key: 'all',
            label: 'Toplam Tahsilat',
            value: stats?.all_total,
            color: 'success',
            sublabel: 'tüm dönemler',
            icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
          },
        ]}
      />

      <div className="stocks-table-wrap">
        <DataTable
          pageKey="cari-receipts-list"
          tableClassName="stocks-table"
          rowClassName="stocks-table-row"
          loading={loading}
          emptyText="Kayıt yok"
          data={receipts}
          rowKey="id"
          onRowClick={r => openDetail(r)}
          columns={[
            { id: 'receipt_no', header: 'Belge No', accessor: 'receipt_no', sortable: true, width: 130,
              cell: r => <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--accent-primary)' }}>{r.receipt_no}</span> },
            { id: 'date', header: 'Tarih', accessor: 'date', sortable: true, width: 110,
              sortFn: (a,b) => new Date(a.date||0)-new Date(b.date||0),
              cell: r => r.date ? new Date(r.date).toLocaleDateString('tr-TR') : '—' },
            { id: 'account', header: 'Cari', accessor: r => (r.account_title||'') + ' ' + (r.account_code||''), sortable: true,
              cell: r => <><strong>{r.account_title || '—'}</strong><div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)' }}>{r.account_code}</div></> },
            { id: 'description', header: 'Açıklama', accessor: 'description', sortable: true,
              cell: r => <span className="stock-secondary">{r.description || '—'}</span> },
            { id: 'total', header: 'Toplam', accessor: r => parseFloat(r.total_amount)||0, sortable: true, width: 130,
              headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right', fontWeight: 700, color: '#16a34a', fontFamily: 'monospace' },
              cell: r => `+${fmt(r.total_amount)}` },
          ]}
        />
      </div>

      {showModal && (
        <Modal
          icon={null}
          title={modalTitle}
          size="xl"
          onClose={closeModal}
          footer={
            <>
              <div className="ui-modal-footer-left">
                {isView && <Button variant="danger" onClick={handleDelete}>🗑 Sil</Button>}
              </div>
              <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {isEditable && <Button variant="ghost" onClick={isEdit ? () => openDetail({ id: selected.id }) : closeModal}>
                  {isEdit ? 'İptal' : 'Vazgeç'}
                </Button>}
                {isEditable && (
                  <CardSplitButton
                    primaryLabel="💾 Kaydet"
                    primaryAction={() => handleSave('close')}
                    loading={saving}
                    options={[
                      { label: 'Kaydet ve Yeni Aç',  icon: '✚', onClick: () => handleSave('new') },
                      { label: 'Kaydet ve Kapat',   icon: '✓', onClick: () => handleSave('close') },
                    ]}
                  />
                )}
                {isView && <Button onClick={enterEditMode}>✎ Düzenle</Button>}
              </div>
            </>
          }
        >
          {/* Hero strip — view modunda */}
          {mode === 'view' && selected && (
            <CardHero
              icon="💵"
              code={selected.receipt_no}
              title={selected.account_title}
              subtitle={selected.date ? new Date(selected.date).toLocaleDateString('tr-TR') : null}
              statusBadge={<CardStatusBadge preset="approved" label="Onaylı" />}
              kpis={[
                { label: 'Toplam Tutar', value: fmt(selected.total_amount), color: 'success', sublabel: 'TRY' },
                selected.line_count > 0 && { label: 'Satır', value: selected.line_count, color: 'info' },
                selected.personnel_name && { label: 'Personel', value: selected.personnel_name.substring(0, 20), color: 'neutral' },
              ].filter(Boolean)}
            />
          )}

          {msg && (
            <div style={{
              marginBottom: 12, padding: '9px 12px', borderRadius: 8, fontSize: '0.84rem',
              background: msg.type === 'error' ? 'var(--danger-bg)' : 'rgba(16,185,129,0.1)',
              color: msg.type === 'error' ? 'var(--danger)' : '#0d9488',
              border: '1px solid ' + (msg.type === 'error' ? 'rgba(239,68,68,0.25)' : 'rgba(16,185,129,0.25)'),
            }}>{msg.text}</div>
          )}

          {/* Header alanları */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
            <FormField label="Belge No">
              <input disabled value={mode !== 'view' ? 'Otomatik' : (selected?.receipt_no || '—')}
                style={{ fontFamily: 'monospace', opacity: 0.7 }} />
            </FormField>
            <FormField label="Tarih" required>
              <input type="date" value={header.date} onChange={e => setHeader({ ...header, date: e.target.value })} disabled={mode === 'view'} />
            </FormField>
            {/* CARI SEÇİMİ — Belge No + Tarih altında tam genişlik */}
            <div style={{ gridColumn: '1 / -1' }}>
              <FormField label="Cari" required>
                {(() => {
                  const selCari = (accounts || []).find(a => String(a.id) === String(header.account_id));
                  return (
                    <>
                      <CariCard
                        cari={selCari}
                        disabled={mode === 'view'}
                        label="Cari"
                        required
                        onPick={() => { setAccountListSearch(''); setShowAccountList(true); }}
                      />
                      {selCari && <BalancePill account={selCari} />}
                    </>
                  );
                })()}
              </FormField>
            </div>
            <FormField label="Açıklama" fullWidth>
              <input value={header.description} onChange={e => setHeader({ ...header, description: e.target.value })} disabled={mode === 'view'} placeholder="Örn: Fatura tahsilatı" />
            </FormField>
            <FormField label="Personel">
              <select value={header.personnel_id} onChange={e => setHeader({ ...header, personnel_id: e.target.value })} disabled={mode === 'view'}>
                <option value="">Seçiniz</option>
                {personnel.map(p => <option key={p.id} value={p.id}>{p.full_name || (p.first_name + ' ' + p.last_name)}</option>)}
              </select>
            </FormField>
            <FormField label={<>Proje {mode !== 'view' && <button className="quick-add-btn" onClick={() => setShowQuickList('project')} type="button">+</button>}</>}>
              <select value={header.project_id} onChange={e => setHeader({ ...header, project_id: e.target.value })} disabled={mode === 'view'}>
                <option value="">Seçiniz</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.code ? p.code + ' - ' : ''}{p.name}</option>)}
              </select>
            </FormField>
            <FormField label={<>Özel Kod 1 {mode !== 'view' && <button className="quick-add-btn" onClick={() => setShowQuickList('special1')} type="button">+</button>}</>}>
              <select value={header.special_code1} onChange={e => setHeader({ ...header, special_code1: e.target.value })} disabled={mode === 'view'}>
                <option value="">Seçiniz</option>
                {specialCodes1.map(s => <option key={s.id} value={s.code || s.name}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
              </select>
            </FormField>
            <FormField label={<>Özel Kod 2 {mode !== 'view' && <button className="quick-add-btn" onClick={() => setShowQuickList('special2')} type="button">+</button>}</>}>
              <select value={header.special_code2} onChange={e => setHeader({ ...header, special_code2: e.target.value })} disabled={mode === 'view'}>
                <option value="">Seçiniz</option>
                {specialCodes2.map(s => <option key={s.id} value={s.code || s.name}>{s.code ? s.code + ' - ' : ''}{s.name}</option>)}
              </select>
            </FormField>
          </div>

          {/* Ödeme Satırları */}
          <div style={{ marginBottom: 12, fontSize: '0.95rem', fontWeight: 700 }}>Ödeme Yöntemleri</div>
          <div style={{ border: '1px solid var(--border-secondary)', borderRadius: 8, maxHeight: 380, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: 1280 }}>
              <thead style={{ background: 'var(--bg-secondary)', position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{ padding: '8px', textAlign: 'left', width: 30 }}>#</th>
                  <th style={{ padding: '8px', textAlign: 'left', width: 160 }}>Yöntem</th>
                  <th style={{ padding: '8px', textAlign: 'left', minWidth: 260 }}>Detay</th>
                  <th style={{ padding: '8px', textAlign: 'right', width: 120 }}>Tutar</th>
                  <th style={{ padding: '8px', textAlign: 'center', width: 80 }}>Döviz</th>
                  <th style={{ padding: '8px', textAlign: 'right', width: 90 }} title="İşlem dövizi → TRY kuru">Kur</th>
                  <th style={{ padding: '8px', textAlign: 'right', width: 130 }} title="Yerli Para Birimi karşılığı">YPB (TRY)</th>
                  <th style={{ padding: '8px', textAlign: 'left', minWidth: 180 }}>Açıklama</th>
                  {mode !== 'view' && <th style={{ width: 40 }}></th>}
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const method = PAYMENT_METHODS.find(p => p.value === l.payment_method);
                  return (
                    <tr key={i} style={{ borderTop: '1px solid var(--border-secondary)' }}>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>{i + 1}</td>
                      <td style={{ padding: '6px 8px' }}>
                        {mode !== 'view' ? (
                          <select value={l.payment_method} onChange={e => setLinePaymentMethod(i, e.target.value)}
                            style={{ width: '100%', padding: '4px 8px', fontSize: '0.82rem' }}>
                            {PAYMENT_METHODS
                              .filter(p => p.value !== 'shopping_voucher' || String(settings.shopping_voucher_enabled || 'false').toLowerCase() === 'true')
                              .map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                          </select>
                        ) : (
                          <span style={{ background: (method?.color || '#64748b') + '22', color: method?.color, padding: '3px 9px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600 }}>
                            {method?.label || l.payment_method}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        {l.payment_method === 'cash' && (
                          mode !== 'view' ? (
                            <>
                              <select value={l.cash_register_id || ''} onChange={e => setLineCashRegister(i, e.target.value)}
                                style={{ width: '100%', padding: '4px 8px', fontSize: '0.82rem', borderColor: !l.cash_register_id ? 'var(--danger)' : undefined }}>
                                <option value="">Kasa seç *</option>
                                {cashRegisters.map(r => <option key={r.id} value={r.id}>{r.register_name} ({r.currency_code})</option>)}
                              </select>
                              {(() => {
                                const reg = cashRegisters.find(r => r.id === l.cash_register_id);
                                if (reg && l.currency_code && reg.currency_code !== l.currency_code) {
                                  return <div style={{ fontSize: '0.7rem', color: 'var(--danger)', marginTop: 2 }}>
                                    ⚠ Kasa {reg.currency_code} — {l.currency_code} kabul etmez. {reg.currency_code} kasa seçin veya satırı {reg.currency_code} yapın.
                                  </div>;
                                }
                                return null;
                              })()}
                            </>
                          ) : <span>{l.cash_register_name || '—'}</span>
                        )}
                        {l.payment_method === 'bank_transfer' && (
                          mode !== 'view' ? (
                            <>
                              <select value={l.bank_account_id || ''} onChange={e => setLineBankAccount(i, e.target.value)}
                                style={{ width: '100%', padding: '4px 8px', fontSize: '0.82rem', borderColor: !l.bank_account_id ? 'var(--danger)' : undefined }}>
                                <option value="">Banka hesabı seç *</option>
                                {bankAccounts.map(b => <option key={b.id} value={b.id}>{b.account_name} — {b.bank_name} ({b.currency_code})</option>)}
                              </select>
                              {(() => {
                                const ba = bankAccounts.find(b => b.id === l.bank_account_id);
                                if (ba && l.currency_code && ba.currency_code !== l.currency_code) {
                                  return <div style={{ fontSize: '0.7rem', color: 'var(--danger)', marginTop: 2 }}>
                                    ⚠ Banka {ba.currency_code} — {l.currency_code} kabul etmez. {ba.currency_code} banka seçin veya satırı {ba.currency_code} yapın.
                                  </div>;
                                }
                                return null;
                              })()}
                            </>
                          ) : <span>{l.bank_account_name || '—'}</span>
                        )}
                        {(l.payment_method === 'check' || l.payment_method === 'note') && (
                          mode !== 'view' ? (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                              <input placeholder="Belge No *" required value={l.check_doc_number || ''} onChange={e => setLine(i, 'check_doc_number', e.target.value)}
                                style={{ padding: '4px 8px', fontSize: '0.78rem', borderColor: !l.check_doc_number ? 'var(--danger)' : undefined }} />
                              <input type="date" required value={l.check_due_date || ''} onChange={e => setLine(i, 'check_due_date', e.target.value)}
                                style={{ padding: '4px 8px', fontSize: '0.78rem', borderColor: !l.check_due_date ? 'var(--danger)' : undefined }} title="Vade *" />
                              <input placeholder="Banka *" required value={l.check_bank_name || ''} onChange={e => setLine(i, 'check_bank_name', e.target.value)}
                                style={{ padding: '4px 8px', fontSize: '0.78rem', borderColor: !l.check_bank_name ? 'var(--danger)' : undefined }} />
                            </div>
                          ) : (
                            <span>
                              <strong>{l.check_doc_number || '—'}</strong>
                              {l.check_bank_name && <span style={{ color:'var(--text-secondary)' }}> · {l.check_bank_name}</span>}
                              {l.check_due_date && <span style={{ color:'var(--text-tertiary)' }}> · Vade: {new Date(l.check_due_date).toLocaleDateString('tr-TR')}</span>}
                            </span>
                          )
                        )}
                        {l.payment_method === 'credit_card' && (
                          mode !== 'view' ? (
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 4 }}>
                              <select value={l.pos_definition_id || ''} onChange={e => { setLine(i, 'pos_definition_id', e.target.value); loadPosCommissions(e.target.value); }}
                                style={{ padding: '4px 8px', fontSize: '0.78rem', borderColor: !l.pos_definition_id ? 'var(--danger)' : undefined }}>
                                <option value="">POS seç *</option>
                                {posList.map(p => <option key={p.id} value={p.id}>{p.pos_name}</option>)}
                              </select>
                              <select value={l.installment_count || 1} onChange={e => setLine(i, 'installment_count', parseInt(e.target.value, 10))}
                                style={{ padding: '4px 8px', fontSize: '0.78rem' }}>
                                {[1,2,3,4,5,6,7,8,9,10,11,12].map(n => <option key={n} value={n}>{n === 1 ? 'Tek' : n + 'x'}</option>)}
                              </select>
                              <input placeholder="Slip No" value={l.slip_no || ''} onChange={e => setLine(i, 'slip_no', e.target.value)} style={{ padding: '4px 8px', fontSize: '0.78rem' }} />
                            </div>
                          ) : (
                            <span>
                              <strong>{l.pos_name || posList.find(p => p.id === l.pos_definition_id)?.pos_name || 'POS —'}</strong>
                              <span style={{ color:'var(--text-secondary)' }}> · {l.installment_count > 1 ? l.installment_count + ' Taksit' : 'Tek Çekim'}</span>
                              {l.slip_no && <span style={{ color:'var(--text-tertiary)' }}> · Slip {l.slip_no}</span>}
                            </span>
                          )
                        )}
                        {l.payment_method === 'shopping_voucher' && (
                          mode !== 'view' ? (
                            availableVouchers.length === 0 ? (
                              <span style={{ color: 'var(--text-tertiary)', fontSize: '0.78rem', fontStyle: 'italic' }}>
                                {header.account_id ? 'Bu carinin kullanılabilir çeki yok' : 'Önce cari seçin'}
                              </span>
                            ) : (
                              <select value={l.shopping_voucher_id || ''} onChange={e => {
                                const v = availableVouchers.find(x => x.id === e.target.value);
                                setLine(i, 'shopping_voucher_id', e.target.value);
                                if (v) setLine(i, 'amount', v.amount);
                              }} style={{ width: '100%', padding: '4px 8px', fontSize: '0.78rem', borderColor: !l.shopping_voucher_id ? 'var(--danger)' : undefined }}>
                                <option value="">Çek seç</option>
                                {availableVouchers.map(v => (
                                  <option key={v.id} value={v.id}>
                                    {v.voucher_no} — {Number(v.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {v.currency_code}
                                    {v.expiry_date ? ` (son: ${new Date(v.expiry_date).toLocaleDateString('tr-TR')})` : ''}
                                  </option>
                                ))}
                              </select>
                            )
                          ) : <span>Çek: <strong>{l.shopping_voucher_no || '—'}</strong></span>
                        )}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input type="number" step="0.01" min="0" value={l.amount || ''} onChange={e => setLine(i, 'amount', e.target.value)} disabled={mode === 'view'}
                          style={{ width: '100%', padding: '4px 8px', textAlign: 'right', fontFamily: 'monospace' }} />
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        <select value={l.currency_code || LOCAL_CURRENCY}
                          onChange={e => setLineCurrency(i, e.target.value)}
                          disabled={mode === 'view' || isCurrencyLocked(l.payment_method)}
                          title={isCurrencyLocked(l.payment_method) ? 'Bu ödeme yöntemi sadece TRY ile çalışır' : ''}
                          style={{ width: '100%', padding: '4px 6px', fontSize: '0.78rem' }}>
                          {currencies.length === 0 && <option value="TRY">TRY</option>}
                          {currencies.map(c => {
                            if (c.code === LOCAL_CURRENCY) return <option key={c.code} value={c.code}>{c.code}</option>;
                            const r = exchangeRates[c.code];
                            const rate = r?.selling_rate || r?.mid_rate || r?.buying_rate;
                            return <option key={c.code} value={c.code}>
                              {c.code}{rate ? ` (${Number(rate).toLocaleString('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 })})` : ''}
                            </option>;
                          })}
                        </select>
                      </td>
                      <td style={{ padding: '6px 4px' }}>
                        <input type="number" step="0.000001" min="0"
                          value={l.exchange_rate || (l.currency_code === LOCAL_CURRENCY ? 1 : '')}
                          onChange={e => setLine(i, 'exchange_rate', e.target.value)}
                          disabled={mode === 'view' || l.currency_code === LOCAL_CURRENCY || isCurrencyLocked(l.payment_method)}
                          style={{ width: '100%', padding: '4px 6px', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.78rem' }} />
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.82rem',
                                   color: l.currency_code !== LOCAL_CURRENCY ? 'var(--accent-primary)' : 'var(--text-secondary)' }}>
                        {fmt((parseFloat(l.amount) || 0) * (parseFloat(l.exchange_rate) || 1))}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input type="text" value={l.notes || ''} onChange={e => setLine(i, 'notes', e.target.value)} disabled={mode === 'view'}
                          placeholder={mode !== 'view' ? 'Satır açıklaması...' : ''}
                          style={{ width: '100%', padding: '4px 8px', fontSize: '0.82rem' }} />
                      </td>
                      {mode !== 'view' && (
                        <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                          <button onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer' }}>✕</button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* ALT: Sol = Satır Ekle + Notlar; Sağ = Toplamlar */}
          <div className="receipt-bottom-row" style={{ display: 'flex', gap: 16, marginTop: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 300px', minWidth: 0 }}>
              {mode !== 'view' && (
                <Button variant="ghost" size="sm" onClick={addLine} className="receipt-add-line">+ Satır Ekle</Button>
              )}
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-tertiary)', margin: '10px 0 4px' }}>Notlar</label>
              <textarea rows="5" value={header.notes} onChange={e => setHeader({ ...header, notes: e.target.value })} disabled={mode === 'view'}
                className="ui-input"
                style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            <div className="receipt-totals-panel" style={{ width: 380, flexShrink: 0 }}>
              {(() => {
                const groups = {};
                lines.forEach(l => {
                  const m = l.payment_method;
                  if (!groups[m]) groups[m] = { byCcy: {}, local: 0 };
                  const ccy = l.currency_code || LOCAL_CURRENCY;
                  groups[m].byCcy[ccy] = (groups[m].byCcy[ccy] || 0) + (parseFloat(l.amount) || 0);
                  groups[m].local += lineLocal(l);
                });
                const groupRows = Object.entries(groups).filter(([, g]) => g.local > 0);
                const checks = lines.filter(l => (l.payment_method === 'check' || l.payment_method === 'note') && l.amount && l.check_due_date);
                let checkLocal = 0, weightedMs = 0;
                checks.forEach(c => {
                  const amtLocal = lineLocal(c);
                  const d = new Date(c.check_due_date).getTime();
                  checkLocal += amtLocal; weightedMs += amtLocal * d;
                });
                const avgMs = checkLocal > 0 ? weightedMs / checkLocal : 0;
                const avgDate = avgMs ? new Date(avgMs).toLocaleDateString('tr-TR') : '—';
                return (
                  <div style={{ border: '1px solid var(--border-secondary)', borderRadius: 8, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <tbody>
                        {groupRows.map(([m, g]) => {
                          const method = PAYMENT_METHODS.find(p => p.value === m);
                          const ccyEntries = Object.entries(g.byCcy).filter(([, v]) => v > 0);
                          return (
                            <tr key={m} style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-secondary)' }}>
                              <td style={{ padding: '8px 14px', fontSize: '0.85rem', color: method?.color, fontWeight: 500 }}>
                                {method?.label || m}:
                                <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
                                  {ccyEntries.map(([c, v]) => `${fmt(v)} ${c}`).join(' + ')}
                                </div>
                              </td>
                              <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 700, color: method?.color, fontFamily: 'monospace' }}>
                                {fmt(g.local)}
                                <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)', fontWeight: 400 }}>TRY</div>
                              </td>
                            </tr>
                          );
                        })}
                        <tr style={{ background: 'var(--bg-secondary)', borderTop: '2px solid var(--border-secondary)' }}>
                          <td style={{ padding: '12px 14px', fontWeight: 700, fontSize: '0.95rem' }}>
                            GENEL TOPLAM (YPB):
                            {hasFx && <div style={{ fontSize: '0.7rem', color: 'var(--text-tertiary)', fontWeight: 400 }}>Yabancı para satırlar TRY'ye çevrildi</div>}
                          </td>
                          <td style={{ padding: '12px 14px', textAlign: 'right', fontWeight: 700, color: '#16a34a', fontFamily: 'monospace', fontSize: '1.1rem' }}>
                            {fmt(total)} TRY
                          </td>
                        </tr>
                        {checks.length > 0 && (
                          <tr style={{ background: 'rgba(124,58,237,0.08)', borderTop: '1px solid var(--border-secondary)' }}>
                            <td colSpan={2} style={{ padding: '8px 14px', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                              <strong>Çek/Senet:</strong> {checks.length} adet, TRY karşılığı toplam <strong>{fmt(checkLocal)}</strong> — Ortalama Vade: <strong>{avgDate}</strong>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          </div>
        </Modal>
      )}

      {showQuickList && (
        <QuickListManager
          open={true}
          title={showQuickList === 'project' ? 'Proje' : showQuickList === 'special1' ? 'Özel Kod 1' : 'Özel Kod 2'}
          items={showQuickList === 'project' ? projects : showQuickList === 'special1' ? specialCodes1 : specialCodes2}
          apiBase={showQuickList === 'project' ? '/invoices/projects' : '/invoices/special-codes'}
          extraBody={showQuickList === 'project' ? {} : { code_type: showQuickList }}
          onClose={() => setShowQuickList(null)}
          onChanged={refetchLookups}
        />
      )}

      {/* CARI LİSTESİ MODALI */}
      {showAccountList && (() => {
        const q = (accountListSearch || '').toLowerCase().trim();
        const filtered = (accounts || []).filter(a => !q ||
          (a.code || '').toLowerCase().includes(q) ||
          (a.title || '').toLowerCase().includes(q) ||
          (a.tax_number || '').includes(q) ||
          (a.id_number || '').includes(q) ||
          (a.phone || '').includes(q) ||
          (a.mobile || '').includes(q));
        const cariCols = [
          { id: 'code', header: 'Kod', accessor: 'code', sortable: true, width: 110,
            cell: a => <span className="stock-code">{a.code}</span> },
          { id: 'title', header: 'Ünvan', accessor: 'title', sortable: true,
            cell: a => <span className="stock-name-cell">{a.title}</span> },
          { id: 'type', header: 'Tip', accessor: 'type', sortable: true, width: 100 },
          { id: 'tax', header: 'VD / TC', sortable: true, width: 150,
            accessor: a => a.identity_type === 'individual' ? (a.id_number || '') : `${a.tax_office || ''} ${a.tax_number || ''}`,
            cell: a => <span className="stock-secondary" style={{ fontSize: '0.78rem' }}>
              {a.identity_type === 'individual' ? (a.id_number || '—') : (a.tax_office || a.tax_number ? `${a.tax_office || ''}${a.tax_office && a.tax_number ? ' / ' : ''}${a.tax_number || ''}` : '—')}
            </span> },
          { id: 'phone', header: 'Telefon', sortable: true, width: 130,
            accessor: a => a.phone || a.mobile || '',
            cell: a => <span className="stock-secondary">{a.phone || a.mobile || '—'}</span> },
          { id: 'balance', header: 'Bakiye', accessor: a => parseFloat(a.balance) || 0, sortable: true, width: 170,
            headerStyle: { textAlign: 'right' }, cellStyle: { textAlign: 'right' },
            cell: a => {
              const b = parseFloat(a.balance) || 0;
              const color = b > 0 ? 'var(--danger)' : b < 0 ? 'var(--success)' : 'var(--text-tertiary)';
              const sfx = b > 0 ? ' B' : b < 0 ? ' A' : '';
              return <span style={{ fontFamily: 'monospace', color, fontWeight: 600 }}>
                {fmtAmount(Math.abs(b))} {a.currency_code || 'TRY'}{sfx}
              </span>;
            } },
        ];
        return (
          <div className="modal-overlay" onClick={() => setShowAccountList(false)}>
            <div className="modal-box" style={{ maxWidth: 980, width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: 0 }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-secondary)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Cari Listesi</h3>
                <span style={{ fontSize: '0.72rem', padding: '2px 8px', borderRadius: 12, background: 'var(--accent-glow)', color: 'var(--accent-primary)', fontWeight: 600 }}>{filtered.length} kayıt</span>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <input autoFocus type="text" placeholder="Kod, ünvan, VD/TC, telefon ile ara..."
                    value={accountListSearch} onChange={e => setAccountListSearch(e.target.value)}
                    style={{ width: '100%', padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-secondary)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.85rem', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>
                <button onClick={() => setShowAccountList(false)} title="Kapat"
                  style={{ width: 32, height: 32, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border-secondary)', borderRadius: 8, background: 'var(--bg-card)', cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  ✕
                </button>
              </div>
              <div style={{ flex: 1, overflow: 'auto' }} className="stocks-table-wrap">
                <DataTable
                  pageKey="cari-receipt-picker"
                  tableClassName="stocks-table"
                  rowClassName="stocks-table-row"
                  emptyText={q ? 'Eşleşen cari bulunamadı' : 'Henüz cari yok'}
                  data={filtered}
                  rowKey="id"
                  onRowClick={a => { setHeader({ ...header, account_id: a.id }); setShowAccountList(false); }}
                  columns={cariCols}
                />
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default CariReceiptPage;
